/**
 * Element Registry
 * Registers all elements with the ElementRegistry from content-kit
 */

import { ElementRegistry, type ElementDefinition } from '@ai-content-hub/content-kit';

// Import all elements
import { textElement } from './text';
import { imageElement } from './image';
import { richTextElement } from './rich-text';
import { audioElement } from './audio';
import { videoElement } from './video';
import { mcqElement } from './mcq';
import { matchElement } from './match';
import { colorDropZoneElement } from './color-drop-zone';
import { imageHotspotElement } from './image-hotspot';
import { fitbElement } from './fill-in-the-blanks';
import { orderingElement } from './ordering';
import { recordAudioElement } from './record-audio';

/**
 * All available elements
 */
export const elements = {
    text: textElement,
    image: imageElement,
    'rich-text': richTextElement,
    audio: audioElement,
    video: videoElement,
    mcq: mcqElement,
    match: matchElement,
    'color-drop-zone': colorDropZoneElement,
    'image-hotspot': imageHotspotElement,
    'fill-in-the-blanks': fitbElement,
    ordering: orderingElement,
    'record-audio': recordAudioElement,
} as const;

/**
 * Element type names
 */
export type ElementType = keyof typeof elements;

/**
 * Get all element types
 */
export function getElementTypes(): ElementType[] {
    return Object.keys(elements) as ElementType[];
}

/**
 * Get element by type
 */
export function getElement(type: ElementType): ElementDefinition<any, any> {
    return elements[type] as ElementDefinition<any, any>;
}

/**
 * Register all elements with the global registry
 */
export function registerAllElements(): void {
    Object.values(elements).forEach((element) => {
        ElementRegistry.register(element as ElementDefinition<any, any>);
    });
}

/**
 * Check if all elements are registered
 */
export function areElementsRegistered(): boolean {
    return getElementTypes().every((type) => {
        const element = elements[type];
        return ElementRegistry.has(element.schema.type);
    });
}

// Auto-register on import
registerAllElements();
